import { useEffect, useRef, useState } from "react";
import { config } from "../config";
import { DISABLED_BUTTON_BG, themeInitialState } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { waitForPersistHydration } from "../utils/quoteCommit";
import { type ThemeText, useTheme } from "lola-framework-ui-test";

const defaultThemeFileName = config.THEME;

/**
 * Feeds the animated highlight (`.rainbow-ready` / `.payment-required`) with the
 * partner palette. Only flat colors are usable as gradient stops, so this reads
 * the raw theme colors instead of the view slots — `whiteView.backgroundBtn` is
 * `primaryMesh`, a gradient string.
 */
const applyBrandSweep = (colors: typeof themeInitialState.colors) => {
  const root = document.documentElement;
  const disabledBg =
    typeof colors?.inactived === "string" && colors.inactived.trim()
      ? colors.inactived.trim()
      : DISABLED_BUTTON_BG;
  const stops: Record<string, string | undefined> = {
    "--brand-sweep-base": colors?.primaryGradient,
    "--brand-sweep-accent": colors?.secondaryGradient,
    "--brand-sweep-highlight": colors?.partnerHighlights || colors?.whiteColor,
    "--text-secondary": colors?.secondaryColor,
    "--btn-disabled-bg": disabledBg,
    // `.loader-spinner` arc; unset until a theme lands so the CSS fallback keeps
    // the neutral grey on an unthemed splash.
    "--spinner-start": colors?.primaryGradient,
    "--spinner-end": colors?.secondaryGradient,
  };

  for (const [name, value] of Object.entries(stops)) {
    if (value) root.style.setProperty(name, value);
  }
};

/**
 * Resolves the theme to render:
 * 1. Wait for session persist, then apply a rehydrated partner theme if present.
 * 2. Otherwise load `public/theme.json` (aborted if a partner theme arrives).
 * 3. When the claimed session later carries a partner theme, it overrides.
 */
export const useReadTheme = () => {
  const { setTheme } = useStore();
  const { session } = useSessionStore();
  const [isLoadingTheme, setIsLoadingTheme] = useState(true);
  const [reedTheme, setReedTheme] = useState(themeInitialState);
  const { generateColorsByView } = useTheme(reedTheme.font as ThemeText);
  const hasPartnerThemeRef = useRef(false);

  const applyTheme = (json: typeof themeInitialState) => {
    setReedTheme(json);
    const colorsConfig = generateColorsByView(json.colors);
    if (colorsConfig) setTheme(colorsConfig);
    applyBrandSweep(json.colors);
    setIsLoadingTheme(false);
  };

  // 1) After persist hydration: partner theme from sessionStorage, else default file
  useEffect(() => {
    let cancelled = false;
    const abort = new AbortController();

    (async () => {
      await waitForPersistHydration(useSessionStore.persist);
      if (cancelled) return;

      const partnerTheme =
        useSessionStore.getState().session.data?.partner?.theme;

      if (partnerTheme && typeof partnerTheme === "object") {
        hasPartnerThemeRef.current = true;
        applyTheme(partnerTheme);
        return;
      }

      try {
        const res = await fetch(`/${defaultThemeFileName}`, {
          signal: abort.signal,
        });
        const json = await res.json();
        if (cancelled || hasPartnerThemeRef.current) return;
        applyTheme(json);
      } catch (err) {
        if (cancelled || abort.signal.aborted) return;
        console.error("Error loading default theme:", err);
        if (!hasPartnerThemeRef.current) {
          applyTheme(themeInitialState);
        }
      }
    })();

    return () => {
      cancelled = true;
      abort.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) When the session provides a partner theme (from claim), override it
  useEffect(() => {
    const partnerTheme = session.data?.partner?.theme;
    if (!partnerTheme || typeof partnerTheme !== "object") return;

    hasPartnerThemeRef.current = true;
    applyTheme(partnerTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.data?.partner?.theme]);

  return { isLoadingTheme, reedTheme };
};
